'use client';

import { parseJson } from '@gnsx/genesys.js';
import * as THREE from 'three';


import { ViewMode } from './models/types';

import type { CameraName } from './models';
import type { CameraViewState, EditorCameraData } from '@/app/editor/core/editorClassData';
import type CameraControlsImpl from 'camera-controls';

type PersistedCameraData = EditorCameraData & { json?: string };

interface CameraPersistenceContext {
  projectName: string;
  sceneName: string;
  cameraName: CameraName;
}

export const CAMERA_STATE_STORAGE_VERSION = 1;
export const CAMERA_STATE_DEFAULT_FLY_SPEED = 1;

const CAMERA_VIEW_MODES: readonly ViewMode[] = [
  ViewMode.Perspective,
  ViewMode.Top,
  ViewMode.Bottom,
  ViewMode.Front,
  ViewMode.Back,
  ViewMode.Left,
  ViewMode.Right,
];

const getCameraStorageBaseKey = ({ cameraName, projectName, sceneName }: CameraPersistenceContext) =>
  `-${projectName}-${sceneName}-camera-${cameraName || 'default'}`.toLowerCase();

const getCameraStorageKey = (context: CameraPersistenceContext) => getCameraStorageBaseKey(context);

const getLegacyCameraStorageKey = (context: CameraPersistenceContext, viewMode: ViewMode) =>
  `${getCameraStorageBaseKey(context)}-${viewMode}`.toLowerCase();

const parseCameraData = (value: string | null): PersistedCameraData | null => {
  if (!value) return null;
  try {
    return parseJson(value) as PersistedCameraData;
  } catch (error) {
    console.warn('Failed to parse camera data:', error);
    return null;
  }
};

const parseLegacyCameraViewState = (json: string): CameraViewState | null => {
  try {
    const parsed = parseJson(json) as {
      position?: number[];
      target?: number[];
      zoom?: number;
    };
    if (!Array.isArray(parsed.position) || parsed.position.length !== 3) return null;
    if (!Array.isArray(parsed.target) || parsed.target.length !== 3) return null;
    const [px, py, pz] = parsed.position;
    const [tx, ty, tz] = parsed.target;
    const zoom = typeof parsed.zoom === 'number' ? parsed.zoom : 1;
    return {
      position: [px, py, pz],
      target: [tx, ty, tz],
      zoom,
    };
  } catch {
    return null;
  }
};

const getDefaultCameraPositionByViewMode = (viewMode: ViewMode): [number, number, number] => {
  switch (viewMode) {
    case ViewMode.Top:
      return [0, 30, 0];
    case ViewMode.Bottom:
      return [0, -30, 0];
    case ViewMode.Front:
      return [0, 0, 30];
    case ViewMode.Back:
      return [0, 0, -30];
    case ViewMode.Left:
      return [-30, 0, 0];
    case ViewMode.Right:
      return [30, 0, 0];
    case ViewMode.Perspective:
    default:
      return [0, 15, 15];
  }
};

const getCameraViewStateFromControls = (controls: CameraControlsImpl): CameraViewState => {
  const position = new THREE.Vector3();
  const target = new THREE.Vector3();
  controls.getPosition(position);
  controls.getTarget(target);
  const zoom = controls.camera instanceof THREE.OrthographicCamera ? controls.camera.zoom : 1;
  return {
    position: [position.x, position.y, position.z],
    target: [target.x, target.y, target.z],
    zoom,
  };
};

const upsertLegacyCameraData = (
  currentData: EditorCameraData | null,
  legacyData: PersistedCameraData,
  fallbackMode: ViewMode,
): { nextData: EditorCameraData, didChange: boolean } => {
  const nextData: EditorCameraData = {
    version: CAMERA_STATE_STORAGE_VERSION,
    flySpeed: currentData?.flySpeed ?? legacyData.flySpeed ?? CAMERA_STATE_DEFAULT_FLY_SPEED,
    viewMode: currentData?.viewMode ?? legacyData.viewMode ?? fallbackMode,
    views: { ...(currentData?.views ?? {}) },
  };
  const legacyViewMode = legacyData.viewMode ?? fallbackMode;
  const legacyState = legacyData.json ? parseLegacyCameraViewState(legacyData.json) : null;
  if (legacyState) {
    nextData.views = {
      ...(nextData.views ?? {}),
      [legacyViewMode]: legacyState,
    };
    return { nextData, didChange: true };
  }
  return { nextData, didChange: false };
};

export const getDefaultCameraViewState = (viewMode: ViewMode): CameraViewState => ({
  position: getDefaultCameraPositionByViewMode(viewMode),
  target: [0, 0, 0],
  zoom: 1,
});

export const migratePersistedCameraState = (context: CameraPersistenceContext): EditorCameraData | null => {
  const key = getCameraStorageKey(context);
  const existingData = parseCameraData(localStorage.getItem(key));
  const hasVersionedData = existingData?.version === CAMERA_STATE_STORAGE_VERSION;
  let nextData: EditorCameraData = {
    version: CAMERA_STATE_STORAGE_VERSION,
    flySpeed: existingData?.flySpeed ?? CAMERA_STATE_DEFAULT_FLY_SPEED,
    viewMode: existingData?.viewMode,
    views: { ...(existingData?.views ?? {}) },
  };
  let didChange = Boolean(existingData && !hasVersionedData);

  if (existingData?.json) {
    const result = upsertLegacyCameraData(nextData, existingData, ViewMode.Perspective);
    nextData = result.nextData;
    didChange = didChange || result.didChange;
  }

  for (const legacyViewMode of CAMERA_VIEW_MODES) {
    const legacyKey = getLegacyCameraStorageKey(context, legacyViewMode);
    const legacyData = parseCameraData(localStorage.getItem(legacyKey));
    if (!legacyData) continue;

    const result = upsertLegacyCameraData(nextData, legacyData, legacyViewMode);
    nextData = result.nextData;
    if (!nextData.viewMode) {
      nextData.viewMode = legacyData.viewMode ?? legacyViewMode;
    }
    didChange = didChange || result.didChange;
    localStorage.removeItem(legacyKey);
  }

  const hasViews = Object.keys(nextData.views ?? {}).length > 0;
  if (didChange) {
    if (hasViews) {
      localStorage.setItem(key, JSON.stringify(nextData));
    } else {
      localStorage.removeItem(key);
      return null;
    }
  }

  return hasViews ? nextData : null;
};

export const savePersistedCameraState = (params: {
  context: CameraPersistenceContext;
  controls: CameraControlsImpl;
  viewMode: ViewMode;
  flySpeed: number;
}): void => {
  const { context, controls, flySpeed, viewMode } = params;
  const existingCameraData = migratePersistedCameraState(context);
  const cameraData: EditorCameraData = {
    version: CAMERA_STATE_STORAGE_VERSION,
    viewMode,
    flySpeed,
    views: {
      ...(existingCameraData?.views ?? {}),
      [viewMode]: getCameraViewStateFromControls(controls),
    },
  };
  localStorage.setItem(getCameraStorageKey(context), JSON.stringify(cameraData));
};

export const clearPersistedCameraState = (params: {
  context: CameraPersistenceContext;
  viewMode: ViewMode;
}): void => {
  const { context, viewMode } = params;
  const cameraData = migratePersistedCameraState(context);
  if (!cameraData?.views) return;

  const views = { ...cameraData.views };
  delete views[viewMode];

  const key = getCameraStorageKey(context);
  const hasViews = Object.keys(views).length > 0;
  if (!hasViews) {
    localStorage.removeItem(key);
    return;
  }

  const nextData: EditorCameraData = {
    version: CAMERA_STATE_STORAGE_VERSION,
    flySpeed: cameraData.flySpeed,
    viewMode: cameraData.viewMode,
    views,
  };
  localStorage.setItem(key, JSON.stringify(nextData));
};

export const loadPersistedCameraState = (params: {
  context: CameraPersistenceContext;
  viewMode: ViewMode;
}): { flySpeed: number, viewState: CameraViewState } => {
  const { context, viewMode } = params;
  const cameraData = migratePersistedCameraState(context);
  return {
    flySpeed: cameraData?.flySpeed ?? CAMERA_STATE_DEFAULT_FLY_SPEED,
    viewState: cameraData?.views?.[viewMode] ?? getDefaultCameraViewState(viewMode),
  };
};
